import React, { useEffect, useMemo } from 'react';
import { Container, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { BoatDetails, HandicapSystem } from '../boatList/BoatDetails';
import { performanceTracker } from '../../services/PerformanceTracker';
import { analyticsService } from '../../services/AnalyticsService';

const times = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 120];
const formatAsProperTime = true;

export function HandicapTable() {
  const boatListState = useSelector((state: RootState) => state.boatList);
  const boatListGroup = boatListState.boatGroupList.find((g) => g.id == boatListState.currentGroupId);
  const baseBoat = boatListGroup?.boatDetails.find((b) => b.id == boatListGroup.baseBoatId);
  const handicapSystem = boatListGroup?.handicapSystem;

  // Memoize handicap calculations with performance tracking
  const { sortedBoats, calculationMetrics } = useMemo(() => {
    const startTime = performance.now();

    if (!boatListGroup?.boatDetails) {
      return { sortedBoats: [], calculationMetrics: null };
    }

    const boats = [...boatListGroup.boatDetails].sort(sortByHandicap);
    const endTime = performance.now();
    const duration = endTime - startTime;

    const metrics = {
      boat_count: boats.length,
      handicap_system: handicapSystem || 'unknown',
      calculation_duration_ms: duration,
      has_base_boat: !!baseBoat,
      calculation_complexity: boats.length * times.length, // Total calculations performed
    };

    // Track performance if calculation took significant time or involved many boats
    if (duration > 1 || boats.length > 10) {
      performanceTracker.trackCustomTiming(
        'handicap_calculation',
        startTime,
        endTime,
        metrics,
      );

      // Also log as analytics event
      analyticsService.logEvent('handicap_calculation_performance', metrics);
    }

    return { sortedBoats: boats, calculationMetrics: metrics };
  }, [boatListGroup?.boatDetails, baseBoat, handicapSystem]);

  // Track handicap table rendering performance
  useEffect(() => {
    if (calculationMetrics && boatListGroup) {
      analyticsService.logEvent('handicap_table_view', {
        handicap_system: calculationMetrics.handicap_system,
        boat_count: calculationMetrics.boat_count,
        has_base_boat: calculationMetrics.has_base_boat,
        calculation_duration_ms: calculationMetrics.calculation_duration_ms,
      });
    }
  }, [calculationMetrics, boatListGroup]);

  function calculateDelta(time: number, handicap: number): number {
    if (baseBoat) {
      let result = 0;
      switch (handicapSystem) {
      case HandicapSystem.IRC:
      case HandicapSystem.TCF:
      case HandicapSystem.NHC:
        result = (handicap > baseBoat.handicap) ?
          time * handicap / baseBoat.handicap - time :
          time * baseBoat.handicap / handicap - time
        ;
        break;
      case HandicapSystem.PY:
      case HandicapSystem.YTC:
        result = (handicap < baseBoat.handicap) ?
          (((time * 1000 / handicap) * baseBoat.handicap / 1000) - time) :
          (((time * 1000 / baseBoat.handicap) * handicap / 1000) - time)
        ;
        break;
      }
      // console.log(`time:${time} handicap:${handicap} base:${baseBoat.handicap} result:${result}`);
      return result;
    } else {
      return 0;
    }
  }

  function sortByHandicap(a: BoatDetails, b: BoatDetails) {
    switch (handicapSystem) {
    case HandicapSystem.IRC:
    case HandicapSystem.TCF:
    case HandicapSystem.NHC:
      return (b.handicap - a.handicap);
    case HandicapSystem.PY:
    case HandicapSystem.YTC:
      return (a.handicap - b.handicap);
    default:
      return 0;
    }
  }

  function boatTableRow(boat: BoatDetails) {
    return <tr
      key={boat.id}
      className={baseBoat && baseBoat.id == boat.id ? 'table-success' : undefined}
    >
      <td>{boat.name}</td>
      <td>{boat.handicap}</td>
      {times.map((t) => <td key={boat.id + '-' + t}>{formatAsTime(calculateDelta(t, boat.handicap))}</td>)}
    </tr>;
  }

  return <Container>
    <h1>Handicap Table</h1>
    {
      boatListGroup ?
        <>
          {baseBoat ?
            <p>Base Handicap : {baseBoat?.handicap}</p> :
            <p>No base boat selected for this group</p>
          }

          {boatListGroup.boatDetails && boatListGroup.boatDetails.length > 0 ?
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Handicap</th>
                  {times.map(timeHeader)}
                </tr>
              </thead>
              <tbody>
                {sortedBoats.map(boatTableRow)}
              </tbody>
            </Table> :
            <p>No boats in the selected group</p>
          }
        </> : <p>No Group selected</p>
    }
  </Container>;
}

function formatAsTime(minutes: number): string {
  let result = '';
  if (formatAsProperTime) {
    const h = Math.floor(minutes / 60);
    const m = Math.floor(minutes % 60);
    const s = Math.floor((minutes * 60) % 60);
    result = `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  } else {
    result = minutes.toFixed(3);
  }
  return result;
}

function timeHeader(minutes: number) {
  const formattedTime = formatAsTime(minutes);
  return <th key={minutes}>{formattedTime}</th>;
}

